import maplibregl from "maplibre-gl";
import { isRoadLayer } from "../shared/editor-geom.ts";
import { STYLE_URL } from "./map-editor.ts";
import {
  downloadFeatureCollection,
  roadsToFeatureCollection,
  snapshotFilename,
  type GeoBBox,
  type SnapshotFeatureCollection,
} from "./snapshot-export.ts";
import {
  resizeOrMove,
  type SnapshotHandle,
  type SnapshotRect,
} from "./snapshot-rect.ts";

export { resizeOrMove } from "./snapshot-rect.ts";
export {
  clipLineStringToBbox,
  downloadFeatureCollection,
  roadsToFeatureCollection,
  snapshotFilename,
} from "./snapshot-export.ts";

const MIN_RECT = 96;
const HANDLE_HIT = 28;

type Handle = SnapshotHandle;
type Rect = SnapshotRect;

/**
 * Movable/resizable snapshot window over the main MapLibre map.
 * Inside the rect: a second map (camera-synced) showing white + black road/path
 * centerlines only. Outside: the normal basemap stays interactive.
 */
export class SnapshotOverlay {
  private readonly host: HTMLElement;
  private readonly mainMap: maplibregl.Map;
  private readonly root: HTMLElement;
  private readonly frame: HTMLElement;
  private readonly mapOffset: HTMLElement;
  private readonly mapEl: HTMLElement;
  private readonly handles: Record<Exclude<Handle, "body">, HTMLElement>;

  private active = false;
  private snapshotMap: maplibregl.Map | null = null;
  private rect: Rect = { left: 0, top: 0, width: 0, height: 0 };
  private drag: { handle: Handle; startX: number; startY: number; start: Rect } | null =
    null;
  private syncing = false;
  private resizeObs: ResizeObserver | null = null;

  private readonly onMainMove = (): void => this.syncCamera();
  private readonly onPointerMove = (e: PointerEvent): void => this.handlePointerMove(e);
  private readonly onPointerUp = (e: PointerEvent): void => this.handlePointerUp(e);

  constructor(host: HTMLElement, mainMap: maplibregl.Map) {
    this.host = host;
    this.mainMap = mainMap;

    this.root = el("div", "snapshot-overlay");
    this.root.hidden = true;

    this.frame = el("div", "snapshot-frame");
    this.mapOffset = el("div", "snapshot-map-offset");
    this.mapEl = el("div", "snapshot-map");
    this.mapOffset.appendChild(this.mapEl);
    this.frame.appendChild(this.mapOffset);
    this.root.appendChild(this.frame);

    this.handles = {
      tl: this.makeHandle("tl"),
      tr: this.makeHandle("tr"),
      bl: this.makeHandle("bl"),
      br: this.makeHandle("br"),
    };

    this.frame.addEventListener("pointerdown", (e) => this.beginDrag(e, "body"));
    for (const [key, node] of Object.entries(this.handles) as Array<
      [Exclude<Handle, "body">, HTMLElement]
    >) {
      this.root.appendChild(node);
      node.addEventListener("pointerdown", (e) => this.beginDrag(e, key));
    }

    host.appendChild(this.root);
  }

  isActive(): boolean {
    return this.active;
  }

  getRect(): SnapshotRect {
    return { ...this.rect };
  }

  /**
   * Road/path vectors inside the snapshot rect as a clipped FeatureCollection.
   * Prefers the road-only snapshot map; falls back to main-map road layers.
   */
  collectRoadGeoJSON(): SnapshotFeatureCollection {
    const bbox = this.rectToGeoBBox();
    const features = this.queryRoadFeatures();
    return roadsToFeatureCollection(features, bbox);
  }

  /** Download clipped roads as `mapus-snapshot-{date}.geojson`. */
  saveRoadGeoJSON(): SnapshotFeatureCollection {
    const fc = this.collectRoadGeoJSON();
    downloadFeatureCollection(fc, snapshotFilename());
    return fc;
  }

  setActive(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    this.root.hidden = !on;
    if (on) {
      this.resetRect();
      this.layout();
      this.ensureMap();
      this.mainMap.on("move", this.onMainMove);
      this.mainMap.on("resize", this.onMainMove);
      this.resizeObs = new ResizeObserver(() => {
        this.clampRect();
        this.layout();
        this.snapshotMap?.resize();
        this.syncCamera();
      });
      this.resizeObs.observe(this.host);
      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.onPointerUp);
      window.addEventListener("pointercancel", this.onPointerUp);
    } else {
      this.teardownMap();
      this.mainMap.off("move", this.onMainMove);
      this.mainMap.off("resize", this.onMainMove);
      this.resizeObs?.disconnect();
      this.resizeObs = null;
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.onPointerUp);
      window.removeEventListener("pointercancel", this.onPointerUp);
      this.drag = null;
    }
  }

  destroy(): void {
    this.setActive(false);
    this.root.remove();
  }

  private makeHandle(kind: Exclude<Handle, "body">): HTMLElement {
    const node = el("div", `snapshot-handle snapshot-handle-${kind}`);
    node.setAttribute("role", "presentation");
    return node;
  }

  private resetRect(): void {
    const { w, h } = this.hostSize();
    const width = Math.max(MIN_RECT, w * 0.55);
    const height = Math.max(MIN_RECT, h * 0.32);
    this.rect = {
      left: (w - width) / 2,
      top: h * 0.28,
      width,
      height,
    };
    this.clampRect();
  }

  private hostSize(): { w: number; h: number } {
    return { w: this.host.clientWidth, h: this.host.clientHeight };
  }

  private clampRect(): void {
    const { w, h } = this.hostSize();
    const width = Math.min(Math.max(MIN_RECT, this.rect.width), w);
    const height = Math.min(Math.max(MIN_RECT, this.rect.height), h);
    const left = Math.min(Math.max(0, this.rect.left), Math.max(0, w - width));
    const top = Math.min(Math.max(0, this.rect.top), Math.max(0, h - height));
    this.rect = { left, top, width, height };
  }

  private layout(): void {
    const { left, top, width, height } = this.rect;
    const { w, h } = this.hostSize();

    this.frame.style.transform = `translate(${left}px, ${top}px)`;
    this.frame.style.width = `${width}px`;
    this.frame.style.height = `${height}px`;

    this.mapOffset.style.width = `${w}px`;
    this.mapOffset.style.height = `${h}px`;
    this.mapOffset.style.transform = `translate(${-left}px, ${-top}px)`;

    this.placeHandle(this.handles.tl, left, top);
    this.placeHandle(this.handles.tr, left + width, top);
    this.placeHandle(this.handles.bl, left, top + height);
    this.placeHandle(this.handles.br, left + width, top + height);
  }

  private placeHandle(node: HTMLElement, x: number, y: number): void {
    node.style.transform = `translate(${x - HANDLE_HIT / 2}px, ${y - HANDLE_HIT / 2}px)`;
  }

  private ensureMap(): void {
    if (this.snapshotMap) {
      this.snapshotMap.resize();
      this.syncCamera();
      return;
    }
    const map = new maplibregl.Map({
      container: this.mapEl,
      style: STYLE_URL,
      center: this.mainMap.getCenter(),
      zoom: this.mainMap.getZoom(),
      bearing: this.mainMap.getBearing(),
      pitch: this.mainMap.getPitch(),
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
    });
    let styled = false;
    map.on("load", () => {
      applyRoadOnlyStyle(map);
      styled = true;
      this.syncCamera();
    });
    // Sprite/glyphs can finish after load; re-apply once when style settles.
    map.on("idle", () => {
      if (!styled) {
        applyRoadOnlyStyle(map);
        styled = true;
      }
    });
    this.snapshotMap = map;
    // First paint after layout.
    requestAnimationFrame(() => {
      map.resize();
      this.syncCamera();
    });
  }

  private teardownMap(): void {
    if (!this.snapshotMap) return;
    this.snapshotMap.remove();
    this.snapshotMap = null;
    this.mapEl.replaceChildren();
  }

  private syncCamera(): void {
    const map = this.snapshotMap;
    if (!map || this.syncing) return;
    this.syncing = true;
    try {
      map.jumpTo({
        center: this.mainMap.getCenter(),
        zoom: this.mainMap.getZoom(),
        bearing: this.mainMap.getBearing(),
        pitch: this.mainMap.getPitch(),
      });
    } finally {
      this.syncing = false;
    }
  }

  private beginDrag(e: PointerEvent, handle: Handle): void {
    if (!this.active || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    this.drag = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      start: { ...this.rect },
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.drag) return;
    e.preventDefault();
    const dx = e.clientX - this.drag.startX;
    const dy = e.clientY - this.drag.startY;
    this.rect = resizeOrMove(this.drag.start, this.drag.handle, dx, dy, this.hostSize());
    this.layout();
  }

  private handlePointerUp(_e: PointerEvent): void {
    if (!this.drag) return;
    this.drag = null;
    this.snapshotMap?.resize();
    this.syncCamera();
  }

  private rectToGeoBBox(): GeoBBox {
    const map = this.snapshotMap ?? this.mainMap;
    const { left, top, width, height } = this.rect;
    const sw = map.unproject([left, top + height]);
    const ne = map.unproject([left + width, top]);
    const minLng = Math.min(sw.lng, ne.lng);
    const maxLng = Math.max(sw.lng, ne.lng);
    const minLat = Math.min(sw.lat, ne.lat);
    const maxLat = Math.max(sw.lat, ne.lat);
    return [minLng, minLat, maxLng, maxLat];
  }

  private queryRoadFeatures(): Array<{
    id?: string | number;
    properties?: Record<string, unknown> | null;
    geometry: { type: string; coordinates: unknown };
  }> {
    const map = this.pickQueryMap();
    if (!map || !map.isStyleLoaded()) return [];
    const layers = (map.getStyle().layers ?? [])
      .map((layer) => layer.id)
      .filter((id) => isRoadLayer(id) && map.getLayer(id));
    if (layers.length === 0) return [];
    const { left, top, width, height } = this.rect;
    const geometry: [[number, number], [number, number]] = [
      [left, top],
      [left + width, top + height],
    ];
    try {
      return map.queryRenderedFeatures(geometry, { layers }) as Array<{
        id?: string | number;
        properties?: Record<string, unknown> | null;
        geometry: { type: string; coordinates: unknown };
      }>;
    } catch {
      return [];
    }
  }

  private pickQueryMap(): maplibregl.Map | null {
    if (this.snapshotMap?.isStyleLoaded()) return this.snapshotMap;
    if (this.mainMap.isStyleLoaded()) return this.mainMap;
    return this.snapshotMap ?? this.mainMap;
  }
}

/** Hide non-transport layers; paint Bright/Liberty road centerlines black on white. */
export function applyRoadOnlyStyle(map: maplibregl.Map): void {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const layer of style.layers) {
    const id = layer.id;
    if (layer.type === "background") {
      map.setPaintProperty(id, "background-color", "#ffffff");
      map.setLayoutProperty(id, "visibility", "visible");
      continue;
    }
    if (isRoadLayer(id)) {
      map.setLayoutProperty(id, "visibility", "visible");
      if (layer.type === "line") {
        try {
          map.setPaintProperty(id, "line-color", "#000000");
          map.setPaintProperty(id, "line-opacity", 1);
        } catch {
          /* some line layers may reject paint keys */
        }
      }
    } else {
      try {
        map.setLayoutProperty(id, "visibility", "none");
      } catch {
        /* layer may not support visibility */
      }
    }
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}
